import { Fragment, useState, useMemo, type DragEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useInstances } from '../hooks/useInstances'
import { useLabels, useSetInstanceLabel, useReorderLabels } from '../hooks/useLabels'
import { useAuthStore } from '../store/authStore'
import InstanceCard from '../components/InstanceCard'
import LabelPicker from '../components/LabelPicker'
import LabelManager from '../components/LabelManager'
import type { Instance, InstanceLabel } from '../api/client'
import StatusBadge from '../components/StatusBadge'
import { useLifecycle } from '../hooks/useInstances'
import { Badge, Button, Card, EmptyState, Input, LinkButton, PageHeader, Select, Skeleton } from '../components/ui'

const VIEW_KEY = 'oaas-view'
const SWIMLANE_KEY = 'oaas-kanban-swimlanes'
type View = 'card' | 'list' | 'kanban'

// Synthetic keys for the two "everything else" buckets: the column holding
// instances with no label, and the swimlane holding instances with no owner.
const NO_LABEL_COL = '__unlabeled__'
const NO_OWNER_LANE = '__unassigned__'
const colKey = (label: InstanceLabel | null) => label?.id ?? NO_LABEL_COL

function ListRow({ instance, onManageLabels }: { instance: Instance; onManageLabels: () => void }) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { start, stop } = useLifecycle()
  const role = useAuthStore((s) => s.role)
  const isProvisioning = instance.status === 'provisioning' || instance.status === 'initializing'

  return (
    <div
      className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={() => navigate(`/instances/${instance.id}`)}
      style={role === 'admin' && instance.label ? { boxShadow: `inset 3px 0 0 ${instance.label.color}` } : undefined}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 truncate">{instance.name}</span>
          <Badge tone="neutral">v{instance.odoo_version}</Badge>
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-xs text-gray-400 truncate">{instance.subdomain_url}</span>
          {role === 'admin' && instance.created_by_email && (
            <span className="text-xs text-brand-500 truncate shrink-0">{instance.created_by_email}</span>
          )}
        </div>
        {isProvisioning && instance.status_message && (
          <p className="text-xs text-gray-400 mt-0.5 truncate">
            {t(`status.msg.${instance.status_message}`, instance.status_message)}
          </p>
        )}
      </div>

      {role === 'admin' && <LabelPicker instance={instance} onManage={onManageLabels} />}
      <StatusBadge status={instance.status} />

      <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
        {instance.status === 'running' && (
          <>
            <a href={instance.custom_url || instance.subdomain_url} target="_blank" rel="noopener noreferrer"
              className="text-xs text-brand-600 hover:text-brand-700 font-medium px-2 py-1 rounded border border-brand-200 hover:bg-brand-50">
              {t('instanceCard.open')}
            </a>
            <Button variant="secondary" size="sm" loading={stop.isPending} onClick={() => stop.mutate(instance.id)}>
              {t('instanceCard.stop')}
            </Button>
          </>
        )}
        {instance.status === 'stopped' && (
          // Secondary shell, brand ink — the row's "go" action reads accented
          // without becoming a filled primary next to the Open chip.
          <Button
            variant="secondary"
            size="sm"
            loading={start.isPending}
            onClick={() => start.mutate(instance.id)}
            className="border-brand-200 text-brand-600 hover:bg-brand-50 hover:text-brand-700"
          >
            {t('instanceCard.start')}
          </Button>
        )}
      </div>
    </div>
  )
}

// The count square is deliberately NOT a Badge: Badge is a text-xs pill, this is
// a fixed 36px rounded-lg number tile. Only the surface is a Card.
function StatTile({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <Card padded={false} className="flex items-center gap-3 px-4 py-3">
      <span className={`flex h-9 w-9 items-center justify-center rounded-lg text-sm font-bold ${tone}`}>
        {value}
      </span>
      <span className="text-sm font-medium text-gray-500">{label}</span>
    </Card>
  )
}

function KanbanCard({ instance, onDragStart, onDragEnd }: { instance: Instance; onDragStart: (id: string) => void; onDragEnd: () => void }) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', instance.id); onDragStart(instance.id) }}
      onDragEnd={onDragEnd}
      onClick={() => navigate(`/instances/${instance.id}`)}
      className="cursor-grab select-none rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="truncate text-sm font-medium text-gray-900">{instance.name}</span>
        <Badge tone="neutral" className="shrink-0">v{instance.odoo_version}</Badge>
      </div>
      <p className="mt-0.5 truncate text-xs text-gray-400">{instance.subdomain}</p>
      {instance.created_by_email && (
        <p className="mt-0.5 truncate text-xs text-brand-500">{instance.created_by_email}</p>
      )}
      <div className="mt-2 flex items-center justify-between">
        <StatusBadge status={instance.status} />
        {instance.status === 'running' && (
          <a
            href={instance.custom_url || instance.subdomain_url}
            target="_blank"
            rel="noopener noreferrer"
            draggable={false}
            onClick={(e) => e.stopPropagation()}
            onDragStart={(e) => e.preventDefault()}
            className="text-xs font-medium text-brand-600 hover:text-brand-700"
          >
            {t('instanceCard.open')}
          </a>
        )}
      </div>
    </div>
  )
}

/**
 * Column header — the label chip, the card count, and (for real labels) the
 * drag handle that reorders columns. With swimlanes ON this renders once above
 * every lane; with them OFF each column renders its own, glued to its cards.
 */
function KanbanColumnHeader({
  label, count, onLabelDragStart, onLabelDragEnd, onLabelDrop, labelDragActive,
}: {
  label: InstanceLabel | null
  count: number
  onLabelDragStart: (labelId: string) => void
  onLabelDragEnd: () => void
  onLabelDrop: (targetLabelId: string) => void
  labelDragActive: boolean
}) {
  const { t } = useTranslation()
  const color = label?.color ?? '#94a3b8'

  return (
    <div
      draggable={!!label}
      onDragStart={label ? (e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', 'label:' + label.id); onLabelDragStart(label.id) } : undefined}
      onDragEnd={label ? onLabelDragEnd : undefined}
      onDragOver={label && labelDragActive ? (e) => { e.preventDefault() } : undefined}
      onDrop={label && labelDragActive ? (e) => { e.preventDefault(); e.stopPropagation(); onLabelDrop(label.id) } : undefined}
      className={`flex select-none items-center gap-2 rounded-lg px-1.5 py-1.5 transition-colors ${label ? 'cursor-grab hover:bg-gray-100 active:cursor-grabbing' : ''}`}
      style={{ borderLeft: `3px solid ${color}` }}
    >
      {label && (
        <svg className="h-3.5 w-3.5 shrink-0 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
          <path d="M7 5a1 1 0 100-2 1 1 0 000 2zm0 6a1 1 0 100-2 1 1 0 000 2zm0 6a1 1 0 100-2 1 1 0 000 2zm6-12a1 1 0 100-2 1 1 0 000 2zm0 6a1 1 0 100-2 1 1 0 000 2zm0 6a1 1 0 100-2 1 1 0 000 2z" />
        </svg>
      )}
      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <span className="truncate text-sm font-semibold text-gray-700">
        {label ? label.name : t('dashboard.kanban.unlabeled')}
      </span>
      <Badge tone="neutral">{count}</Badge>
    </div>
  )
}

/**
 * One drop target: a whole column on the flat board, or a single lane x column
 * cell once swimlanes are on. `over` is owned by the BOARD rather than by the
 * cell so that hovering any cell of a column highlights that column in every
 * lane at once — a target split across lanes otherwise gives no feedback about
 * where the card is actually going to land.
 *
 * Note a cross-lane drop only reassigns the LABEL. Ownership is not draggable,
 * so the card lands in the hovered column but snaps back to its own lane.
 */
function KanbanColumn({
  instances, header, over, compact, labelDragActive,
  onEnter, onLeave, onDrop, onCardDragStart, onCardDragEnd,
}: {
  instances: Instance[]
  header: ReactNode | null
  over: boolean
  compact: boolean
  labelDragActive: boolean
  onEnter: () => void
  onLeave: () => void
  onDrop: () => void
  onCardDragStart: (id: string) => void
  onCardDragEnd: () => void
}) {
  const { t } = useTranslation()

  // The WHOLE cell is the drop target (header + gaps included) so a drop
  // anywhere over it assigns the label — not just the card area.
  const dropProps = labelDragActive ? {} : {
    onDragOver: (e: DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (!over) onEnter() },
    onDragLeave: (e: DragEvent) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) onLeave() },
    onDrop: (e: DragEvent) => { e.preventDefault(); onDrop() },
  }

  return (
    <div
      {...dropProps}
      className={`flex w-72 shrink-0 flex-col rounded-xl border-2 border-dashed p-2 transition-colors ${over ? 'border-brand-400 bg-brand-50/60' : 'border-transparent'}`}
    >
      {header && <div className="mb-2">{header}</div>}
      <div className={`flex flex-1 flex-col gap-2 ${compact ? 'min-h-[3.5rem]' : 'min-h-[100px]'}`}>
        {instances.length === 0
          ? (compact ? null : <p className="py-6 text-center text-xs text-gray-300">{t('dashboard.kanban.emptyColumn')}</p>)
          : instances.map((inst) => <KanbanCard key={inst.id} instance={inst} onDragStart={onCardDragStart} onDragEnd={onCardDragEnd} />)}
      </div>
    </div>
  )
}

export default function DashboardPage() {
  const [dragInstance, setDragInstance] = useState<string | null>(null)
  const [dragLabel, setDragLabel] = useState<string | null>(null)
  const { data: instances, isLoading, error } = useInstances(dragInstance !== null || dragLabel !== null)
  const { data: labels } = useLabels()
  const setLabel = useSetInstanceLabel()
  const reorderLabels = useReorderLabels()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const role = useAuthStore((s) => s.role)

  const [rawView, setRawView] = useState<View>(() => (localStorage.getItem(VIEW_KEY) as View) ?? 'card')
  const [search, setSearch] = useState('')
  const [userFilter, setUserFilter] = useState('')
  const [managingLabels, setManagingLabels] = useState(false)

  // Swimlanes are an opt-in overlay on the kanban board: same label columns,
  // sliced into one horizontal lane per customer. Off by default, remembered.
  const [swimlanes, setSwimlanes] = useState(() => localStorage.getItem(SWIMLANE_KEY) === '1')
  const [collapsedLanes, setCollapsedLanes] = useState<Set<string>>(() => new Set())
  // Which column the dragged card is currently over. Held here rather than in
  // the cell so the highlight spans the column across every lane.
  const [overCol, setOverCol] = useState<string | null>(null)

  // Labels + kanban are an admin-only fleet-organization feature for now.
  const isAdmin = role === 'admin'
  const view = isAdmin || rawView !== 'kanban' ? rawView : 'card'

  const toggleView = (v: View) => {
    setRawView(v)
    localStorage.setItem(VIEW_KEY, v)
  }

  const toggleSwimlanes = () => {
    setSwimlanes((on) => {
      localStorage.setItem(SWIMLANE_KEY, on ? '0' : '1')
      return !on
    })
  }

  const toggleLane = (key: string) => {
    setCollapsedLanes((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const userEmails = useMemo(() => {
    if (!instances) return []
    const set = new Set<string>()
    instances.forEach((i) => { if (i.created_by_email) set.add(i.created_by_email) })
    return Array.from(set).sort()
  }, [instances])

  const filtered = useMemo(() => {
    if (!instances) return []
    return instances.filter((i) => {
      const matchesSearch = !search || i.name.toLowerCase().includes(search.toLowerCase())
      const matchesUser = !userFilter || i.created_by_email === userFilter
      return matchesSearch && matchesUser
    })
  }, [instances, search, userFilter])

  const stats = useMemo(() => {
    const all = instances ?? []
    return {
      total: all.length,
      running: all.filter((i) => i.status === 'running').length,
      stopped: all.filter((i) => i.status === 'stopped').length,
      error: all.filter((i) => i.status === 'error').length,
    }
  }, [instances])

  const hasFilters = search || userFilter

  // Board columns: one per label, plus the trailing catch-all for unlabeled.
  const columns = useMemo<(InstanceLabel | null)[]>(() => [...(labels ?? []), null], [labels])

  // Board rows. Swimlanes off = one anonymous lane holding everything, so the
  // board below renders identically to the flat layout with no extra branches.
  const lanes = useMemo(() => {
    if (!swimlanes) return [{ key: '__all__', title: '', instances: filtered }]
    const byOwner = new Map<string, Instance[]>()
    filtered.forEach((i) => {
      const key = i.created_by_email ?? NO_OWNER_LANE
      const bucket = byOwner.get(key)
      if (bucket) bucket.push(i)
      else byOwner.set(key, [i])
    })
    return Array.from(byOwner.entries())
      .sort(([a], [b]) => {
        if (a === NO_OWNER_LANE) return 1
        if (b === NO_OWNER_LANE) return -1
        return a.localeCompare(b)
      })
      .map(([key, instances]) => ({
        key,
        title: key === NO_OWNER_LANE ? t('dashboard.kanban.noCustomer') : key,
        instances,
      }))
  }, [filtered, swimlanes, t])

  const onInstanceDrop = (labelId: string | null) => {
    const id = dragInstance
    setDragInstance(null)
    setOverCol(null)
    if (!id) return
    const inst = filtered.find((i) => i.id === id)
    if (!inst || (inst.label?.id ?? null) === labelId) return
    setLabel.mutate({ instanceId: inst.id, labelId })
  }

  const onLabelDrop = (targetLabelId: string) => {
    const src = dragLabel
    setDragLabel(null)
    if (!src || src === targetLabelId || !labels) return
    const order = labels.map((l) => l.id)
    const from = order.indexOf(src)
    const to = order.indexOf(targetLabelId)
    if (from === -1 || to === -1) return
    order.splice(from, 1)
    order.splice(to, 0, src)
    reorderLabels.mutate(order)
  }

  // Deliberately NOT SegmentedControl: that primitive is the white-chip-on-grey
  // toggle, and this is a joined icon group in a bordered box sized to match the
  // bordered toolbar buttons beside it. Swapping it would be a redesign of the
  // toolbar, and SegmentedControl has no per-option title, so the icon-only
  // options would lose their accessible names. `aria-pressed` is added so the
  // selected view is exposed the same way the swimlanes toggle already does it.
  const viewBtn = (v: View, title: string, path: string) => (
    <button
      type="button"
      onClick={() => toggleView(v)}
      title={title}
      aria-pressed={view === v}
      className={`px-2.5 py-1.5 transition-colors ${v !== 'card' ? 'border-l border-gray-200' : ''} ${view === v ? 'bg-brand-50 text-brand-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
      </svg>
    </button>
  )

  return (
    <div className="p-4 sm:p-8">
      <PageHeader
        title={t('dashboard.title')}
        description={
          hasFilters
            ? t('dashboard.countFiltered', { shown: filtered.length, total: instances?.length ?? 0 })
            : t('dashboard.count', { count: instances?.length ?? 0 })
        }
        actions={
          <>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              {viewBtn('card', t('dashboard.cardView'), 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z')}
              {viewBtn('list', t('dashboard.listView'), 'M4 6h16M4 12h16M4 18h16')}
              {isAdmin && viewBtn('kanban', t('dashboard.kanbanView'), 'M4 5a1 1 0 011-1h3a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm6 0a1 1 0 011-1h3a1 1 0 011 1v8a1 1 0 01-1 1h-3a1 1 0 01-1-1V5zm6 0a1 1 0 011-1h3a1 1 0 011 1v11a1 1 0 01-1 1h-3a1 1 0 01-1-1V5z')}
            </div>

            {isAdmin && view === 'kanban' && (
              <Button
                variant="secondary"
                onClick={toggleSwimlanes}
                title={t('dashboard.kanban.swimlanes')}
                aria-pressed={swimlanes}
                // brand-* outranks the variant's gray-* here: `brand` is declared
                // after `gray` in tailwind.config, so its utilities are emitted
                // later and win at equal specificity.
                className={swimlanes ? 'border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100' : ''}
                icon={
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h16v5H4V5zm0 9h16v5H4v-5z" />
                  </svg>
                }
              >
                <span className="hidden sm:inline">{t('dashboard.kanban.swimlanes')}</span>
              </Button>
            )}

            {isAdmin && (
              <Button
                variant="secondary"
                onClick={() => setManagingLabels(true)}
                title={t('dashboard.labels.title')}
                icon={
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                }
              >
                <span className="hidden sm:inline">{t('dashboard.labels.title')}</span>
              </Button>
            )}

            <Button onClick={() => navigate('/instances/new')}>{t('dashboard.newInstance')}</Button>
            </>
        }
      />

      {/* Stats strip — quick health overview */}
      {instances && instances.length > 0 && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {/* indigo was a fifth accent family for the same "info" job the
              palette contract assigns to brand. */}
          <StatTile label={t('dashboard.stats.total')} value={stats.total} tone="bg-brand-100 text-brand-700" />
          <StatTile label={t('dashboard.stats.running')} value={stats.running} tone="bg-green-100 text-green-700" />
          <StatTile label={t('dashboard.stats.stopped')} value={stats.stopped} tone="bg-gray-100 text-gray-600" />
          <StatTile label={t('dashboard.stats.error')} value={stats.error} tone="bg-red-100 text-red-700" />
        </div>
      )}

      {/* Search + user filter */}
      {instances && instances.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('dashboard.searchPlaceholder')}
              className="pl-8"
            />
          </div>

          {role === 'admin' && userEmails.length > 0 && (
            <Select
              fullWidth={false}
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
            >
              <option value="">{t('dashboard.allUsers')}</option>
              {userEmails.map((email) => (
                <option key={email} value={email}>{email}</option>
              ))}
            </Select>
          )}

          {hasFilters && (
            <LinkButton tone="neutral" className="px-2" onClick={() => { setSearch(''); setUserFilter('') }}>
              {t('dashboard.clearFilters')}
            </LinkButton>
          )}
        </div>
      )}

      {isLoading && (view === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <Skeleton className="h-4 w-1/2 mb-2" />
              <Skeleton className="h-3 w-3/4" />
            </Card>
          ))}
        </div>
      ) : (
        <Card padded={false} className="divide-y divide-gray-100">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="px-5 py-4">
              <Skeleton className="h-4 w-1/2 mb-2" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          ))}
        </Card>
      ))}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
          {t('dashboard.loadError')}
        </div>
      )}

      {instances && instances.length === 0 && (
        <EmptyState
          icon={<span className="text-4xl">📦</span>}
          title={t('dashboard.empty.title')}
          description={t('dashboard.empty.subtitle')}
          action={<Button onClick={() => navigate('/instances/new')}>{t('dashboard.empty.cta')}</Button>}
        />
      )}

      {instances && instances.length > 0 && filtered.length === 0 && (
        <EmptyState title={t('dashboard.noResults')} />
      )}

      {filtered.length > 0 && view === 'card' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((inst) => (
            <InstanceCard key={inst.id} instance={inst} showOwner={role === 'admin'} showLabels={isAdmin} onManageLabels={() => setManagingLabels(true)} />
          ))}
        </div>
      )}

      {filtered.length > 0 && view === 'list' && (
        <Card padded={false} className="divide-y divide-gray-100">
          {filtered.map((inst) => (
            <ListRow key={inst.id} instance={inst} onManageLabels={() => setManagingLabels(true)} />
          ))}
        </Card>
      )}

      {filtered.length > 0 && view === 'kanban' && (
        <div className="overflow-x-auto pb-4">
          {/* w-max sizes the board to its widest row so the columns stay in
              register across lanes however far it is scrolled. Every row below
              - header strip and each lane's cells - uses the SAME px-2 outer
              padding and the same `w-72 border-2 p-2` per column, so alignment
              never depends on two subtrees agreeing on arithmetic. */}
          <div className="w-max min-w-full">
            {/* Swimlanes hoist the column headers up here so they are stated
                once for the whole board instead of repeating in every lane. */}
            {swimlanes && (
              <div className="flex gap-4 border-b border-gray-200 px-2 pb-2">
                {columns.map((col, idx) => (
                  <Fragment key={colKey(col)}>
                    {/* Same divider as the lane rows below - both rows must
                        carry it or the columns drift out of register. */}
                    {idx > 0 && <div aria-hidden className="w-px shrink-0 self-stretch bg-gray-200" />}
                    <div className="w-72 shrink-0 border-2 border-transparent px-2">
                      <KanbanColumnHeader
                        label={col}
                        count={filtered.filter((i) => (i.label?.id ?? NO_LABEL_COL) === colKey(col)).length}
                        onLabelDragStart={setDragLabel}
                        onLabelDragEnd={() => setDragLabel(null)}
                        onLabelDrop={onLabelDrop}
                        labelDragActive={dragLabel !== null}
                      />
                    </div>
                  </Fragment>
                ))}
              </div>
            )}

            {lanes.map((lane) => {
              const collapsed = collapsedLanes.has(lane.key)
              return (
                <div key={lane.key} className={swimlanes ? 'border-b border-gray-100 last:border-b-0' : ''}>
                  {swimlanes && (
                    <button
                      onClick={() => toggleLane(lane.key)}
                      aria-expanded={!collapsed}
                      className="flex w-full py-2 text-left"
                    >
                      {/* Pinned to the left edge: scrolling right through a wide
                          board must never leave a lane's cards unattributed. */}
                      <span className="sticky left-0 flex items-center gap-2 rounded-lg bg-gray-50 px-2 py-1 transition-colors hover:bg-gray-100">
                        <svg
                          className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${collapsed ? '-rotate-90' : ''}`}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                        <span className="truncate text-sm font-semibold text-gray-700">{lane.title}</span>
                        <span className="rounded-full bg-white px-1.5 text-xs font-medium text-gray-500">
                          {lane.instances.length}
                        </span>
                      </span>
                    </button>
                  )}

                  {!collapsed && (
                    <div className={`flex gap-4 ${swimlanes ? 'px-2 pb-3' : ''}`}>
                      {columns.map((col, idx) => {
                        const key = colKey(col)
                        return (
                          <Fragment key={key}>
                          {idx > 0 && <div aria-hidden className="w-px shrink-0 self-stretch bg-gray-200" />}
                          <KanbanColumn
                            instances={lane.instances.filter((i) => (i.label?.id ?? NO_LABEL_COL) === key)}
                            header={
                              swimlanes ? null : (
                                <KanbanColumnHeader
                                  label={col}
                                  count={lane.instances.filter((i) => (i.label?.id ?? NO_LABEL_COL) === key).length}
                                  onLabelDragStart={setDragLabel}
                                  onLabelDragEnd={() => setDragLabel(null)}
                                  onLabelDrop={onLabelDrop}
                                  labelDragActive={dragLabel !== null}
                                />
                              )
                            }
                            over={overCol === key}
                            compact={swimlanes}
                            labelDragActive={dragLabel !== null}
                            onEnter={() => setOverCol(key)}
                            onLeave={() => setOverCol((c) => (c === key ? null : c))}
                            onDrop={() => onInstanceDrop(col?.id ?? null)}
                            onCardDragStart={setDragInstance}
                            onCardDragEnd={() => { setDragInstance(null); setOverCol(null) }}
                          />
                          </Fragment>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {managingLabels && <LabelManager onClose={() => setManagingLabels(false)} />}
    </div>
  )
}
